import { useCallback } from 'react';
import { IconButton, Typography, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'app/modules/react-redux';
import * as Actions from 'app/store/actions';
import { ToastStatusPendingIcon, ToastStatusDoneIcon } from 'app/components/Icons';
import { type ProgressToast as ProgressToastType } from 'app/store/reducers/progressToasts.reducer';

const PROGRESS_PURPLE = '#8E5EF4';

const useStyles = makeStyles(() => ({
	root: {
		position: 'relative',
		minWidth: 360,
		maxWidth: 480,
		background: '#1F1F1F',
		color: '#FFFFFF',
		borderRadius: 8,
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
		overflow: 'hidden',
	},
	row: {
		display: 'flex',
		alignItems: 'start',
		gap: 12,
		padding: '12px 12px 14px 14px',
	},
	icon: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	message: {
		flex: 1,
		minWidth: 0,
		fontSize: 14,
		lineHeight: 1.35,
		wordBreak: 'break-word',
		marginTop: 2,
	},
	link: {
		color: `${PROGRESS_PURPLE} !important`,
		textDecoration: 'underline',
		fontWeight: 600,
	},
	close: {
		color: '#FFFFFF',
		flexShrink: 0,
		padding: 4,
	},
	bar: {
		height: 4,
		backgroundColor: 'rgba(255, 255, 255, 0.08)',
		'& .MuiLinearProgress-bar': {
			backgroundColor: PROGRESS_PURPLE,
		},
	},
	barIdle: {
		height: 4,
		background: PROGRESS_PURPLE,
	},
}));

type Props = { toast: ProgressToastType };

const keyFor = (toast: ProgressToastType): string => {
	const cardinality = toast.count > 1 ? 'multiple' : 'single';
	const phase = toast.status === 'done' ? 'success' : 'in-progress';
	return `files:${toast.kind}:${phase}:${cardinality}`;
};

const valuesFor = (toast: ProgressToastType, fallbackDest: string): { [key: string]: string | number } => {
	const folderName = toast.destination?.label ?? fallbackDest;
	if (toast.kind === 'upload' || toast.kind === 'delete' || toast.kind === 'trash') {
		return toast.count > 1 ? { count: toast.count, folderName } : { fileName: toast.itemName ?? '', folderName };
	}
	if (
		toast.kind === 'move' ||
		toast.kind === 'copy' ||
		toast.kind === 'create' ||
		toast.kind === 'folder' ||
		toast.kind.includes('rename')
	) {
		return toast.count > 1 ? { count: toast.count, folderName } : { itemName: toast.itemName ?? '', folderName };
	}
	// delete
	return toast.count > 1 ? { count: toast.count } : { fileName: toast.itemName ?? '' };
};

const ProgressToast = ({ toast }: Props) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation('alert');

	const handleDismiss = useCallback(() => {
		dispatch(Actions.dismissProgressToast(toast.id));
	}, [dispatch, toast.id]);

	const i18nKey = keyFor(toast);
	const fallbackDest = t('files:my files page:title', { defaultValue: 'My Files' });
	const values = valuesFor(toast, fallbackDest);
	const destFolderId = toast.destination?.folderId;

	const messageNode =
		destFolderId &&
		(toast.kind === 'upload' ||
			toast.kind === 'move' ||
			toast.kind === 'copy' ||
			toast.kind === 'create' ||
			toast.kind === 'rename-folder') ? (
			<Trans
				i18nKey={i18nKey}
				ns="alert"
				values={values}
				components={{
					1: (
						<RouterLink
							to={`/admin/files?folderId=${encodeURIComponent(destFolderId)}&folderName=${encodeURIComponent(toast.destination?.label ?? '')}`}
							className={classes.link}
						/>
					),
					2: (
						<RouterLink
							to={
								toast.itemId
									? `/admin/files?folderId=${encodeURIComponent(toast.itemId ?? '')}&folderName=${encodeURIComponent(toast.itemName ?? '')}`
									: '/admin/files'
							}
							className={classes.link}
						/>
					),
				}}
			/>
		) : toast.kind === 'delete' ? (
			<Trans
				i18nKey={i18nKey}
				ns="alert"
				values={values}
				components={{
					1: <RouterLink to="/admin/trash" className={classes.link} />,
					2: (
						<RouterLink
							to={`/admin/files?folderId=${encodeURIComponent(destFolderId ?? '')}&folderName=${encodeURIComponent(toast.destination?.label ?? '')}`}
							className={classes.link}
						/>
					),
				}}
			/>
		) : toast.kind === 'trash' ? (
			<Trans
				i18nKey={i18nKey}
				ns="alert"
				values={values}
				components={{
					1: <RouterLink to="/admin/trash" className={classes.link} />,
				}}
			/>
		) : toast.kind === 'perma-delete' || toast.kind === 'restore' ? (
			<Trans
				i18nKey={i18nKey}
				ns="alert"
				values={values}
				components={{
					1: <RouterLink to="/admin/trash" className={classes.link} />,
					2: <RouterLink to="/admin/files" className={classes.link} />,
				}}
			/>
		) : (
			t(i18nKey, values as { [key: string]: unknown })
		);

	const barless = ['create', 'rename-folder', 'rename-file'].includes(toast.kind);
	const showRealBar = toast.status === 'pending' && typeof toast.progress === 'number' && !barless;
	const showIndeterminateBar = toast.status === 'pending' && toast.progress === null && !barless;
	const showDoneBar = toast.status === 'done' && !barless;

	return (
		<div className={classes.root} role="status" aria-live="polite">
			<div className={classes.row}>
				<span className={classes.icon}>
					{toast.status === 'done' ? (
						<ToastStatusDoneIcon style={{ fontSize: 24 }} />
					) : (
						<ToastStatusPendingIcon style={{ fontSize: 24 }} />
					)}
				</span>
				<Typography className={`${classes.message} whitespace-pre-wrap`} component="div">
					{messageNode}
				</Typography>
				<IconButton aria-label="Dismiss" size="small" className={classes.close} onClick={handleDismiss}>
					<CloseIcon style={{ fontSize: 18 }} />
				</IconButton>
			</div>
			{showRealBar && (
				<LinearProgress
					variant="determinate"
					value={Math.min(100, Math.max(0, Math.round((toast.progress as number) * 100)))}
					className={classes.bar}
				/>
			)}
			{showIndeterminateBar && <LinearProgress variant="indeterminate" className={classes.bar} />}
			{showDoneBar && <div className={classes.barIdle} />}
		</div>
	);
};

export default ProgressToast;
